import React, { useState, useEffect } from "react";
import { Input } from "src/components";
import styled from "styled-components";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import { useRecoilState } from "recoil";
import { recoilStates } from "src/recoil/recoilStates";
interface Step1Props {
  onNext: (data: { proposerNameValue: string; contactNumberValue: string }) => void;
  formData?: { proposerNameValue?: string; contactNumberValue?: string };
}

export default function Step1RegistrantInfo({ onNext, formData }: Step1Props) {
  const [user] = useRecoilState(recoilStates.user);

  const { value: proposerNameValue, onChange: onChangeProposerName } = Input.hooks.useInput(
    formData?.proposerNameValue || ""
  );
  const { value: contactNumberValue, onChange: onChangeContactNumber } = Input.hooks.useInput(
    formData?.contactNumberValue || ""
  );

  // 모달 상태 관리
  //
  // [기존 alert() 방식]
  // - 브라우저 자체 기능을 호출하여 즉시 출력
  // - 별도의 상태 관리 없이 바로 실행
  // - 예: alert("단체명을 입력해주세요.");
  //
  // [커스텀 AlertModal 방식]
  // - React 상태(state)를 통해 on/off 제어
  // - 상태 변경 시 컴포넌트가 재렌더링되면서 모달 표시/숨김
  // - 더 나은 UX와 디자인 커스터마이징 가능
  const [showNameErrorModal, setShowNameErrorModal] = useState(false);
  const [showContactErrorModal, setShowContactErrorModal] = useState(false);

  // formData가 변경될 때 초기값으로만 설정 (사용자 수정 가능하도록)
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // 이미 초기화했으면 실행하지 않음
    if (hasInitialized) return;

    // formData가 있고 초기값이 있을 때만 설정
    if (formData?.proposerNameValue && !proposerNameValue) {
      onChangeProposerName({
        target: { value: formData.proposerNameValue },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    if (formData?.contactNumberValue && !contactNumberValue) {
      onChangeContactNumber({
        target: { value: formData.contactNumberValue },
      } as React.ChangeEvent<HTMLInputElement>);
    }

    // 초기화 완료로 표시 (user 정보가 없어도 사용자가 입력할 수 있도록)
    setHasInitialized(true);
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // user 정보가 있고 연락처가 이미 설정되어 있으면 수정 불가
    if (hasUserInfo) {
      return; // 수정 불가능
    }

    // 숫자만 입력 가능하도록 필터링하고 11자리까지만 입력 가능
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9]/g, "").slice(0, 11);

    // 이벤트 객체의 value를 숫자만 남긴 값으로 업데이트
    const updatedEvent = {
      ...e,
      target: {
        ...e.target,
        value: numericValue,
      },
    };

    onChangeContactNumber(updatedEvent);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // user 정보가 있고 이름이 이미 설정되어 있으면 수정 불가
    if (hasUserInfo) {
      return; // 수정 불가능
    }

    // 한글, 영문, 공백 모두 허용하도록 필터링 제거
    // 한글 입력 문제를 해결하기 위해 직접 onChangeProposerName 호출
    onChangeProposerName(e);
  };

  // 사용자 정보가 있는지 확인 (user.realname이 있으면 계속 수정 불가능)
  const hasUserInfo = Boolean(user?.realname?.trim());

  // 다음 버튼 활성화 조건: 신청자 이름과 연락처가 모두 입력되고 연락처가 10자리 또는 11자리여야 함
  const isNextButtonEnabled =
    proposerNameValue?.trim() &&
    contactNumberValue?.trim() &&
    (contactNumberValue.length === 10 || contactNumberValue.length === 11);

  const handleNext = () => {
    // [기존 방식] alert() - 브라우저 자체 기능 즉시 호출
    // if (!proposerNameValue?.trim()) {
    //   alert("신청자 이름을 입력해주세요.");
    //   return;
    // }

    // [현재 방식] AlertModal - React 상태 기반 렌더링
    if (!proposerNameValue?.trim()) {
      setShowNameErrorModal(true); // 상태 변경 → 컴포넌트 재렌더링 → 모달 표시
      return;
    }
    if (!contactNumberValue?.trim()) {
      setShowContactErrorModal(true); // 상태 변경 → 컴포넌트 재렌더링 → 모달 표시
      return;
    }
    if (contactNumberValue.length < 10 || contactNumberValue.length > 11) {
      setShowContactErrorModal(true); // 연락처가 10자리 또는 11자리가 아닌 경우
      return;
    }

    onNext({
      proposerNameValue: proposerNameValue.trim(),
      contactNumberValue: contactNumberValue.trim(),
    });
  };

  return (
    <StepContainer>
      <MainContent>
        {/* 
          React 상태 기반 모달 렌더링
          - showNameErrorModal이 true일 때만 AlertModal 컴포넌트가 렌더링됨
          - showContactErrorModal이 true일 때만 AlertModal 컴포넌트가 렌더링됨
          - 상태가 false이면 해당 모달은 DOM에 존재하지 않음 (조건부 렌더링)
        */}

        {/* 이름 입력 오류 모달 */}
        {showNameErrorModal && (
          <AlertModal
            onClose={() => setShowNameErrorModal(false)}
            title="입력 오류"
            subtitle="신청자 이름을 입력해주세요."
            lines={["신청자 이름은 필수 입력 항목입니다."]}
            confirmText="확인"
            onConfirm={() => setShowNameErrorModal(false)}
          />
        )}

        {/* 연락처 입력 오류 모달 */}
        {showContactErrorModal && (
          <AlertModal
            onClose={() => setShowContactErrorModal(false)}
            title="입력 오류"
            subtitle="신청인 연락처를 입력해주세요."
            lines={["신청인 연락처는 필수 입력 항목입니다."]}
            confirmText="확인"
            onConfirm={() => setShowContactErrorModal(false)}
          />
        )}

        <PageTitle>등록자 정보를 입력해주세요.</PageTitle>

        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>신청자 이름</LabelText>
          </FieldLabel>
          <InputField $active={Boolean(proposerNameValue)} $disabled={hasUserInfo}>
            <StyledInput
              id="proposer-name"
              value={proposerNameValue}
              onChange={handleNameChange}
              placeholder="신청자 이름을 입력하세요."
              disabled={hasUserInfo}
            />
          </InputField>
        </FormField>

        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>신청인 연락처</LabelText>
          </FieldLabel>
          <InputField
            $active={
              Boolean(contactNumberValue) &&
              (contactNumberValue.length === 10 || contactNumberValue.length === 11)
            }
            $disabled={hasUserInfo}
          >
            <StyledInput
              id="contact-number"
              value={contactNumberValue}
              onChange={handleInputChange}
              placeholder="연락처를 입력하세요. (예: 01012345678)"
              disabled={hasUserInfo}
            />
          </InputField>
        </FormField>

        <StepIndicator>
          <StepTitle>단체 등록 과정 3 STEP</StepTitle>
          <StepList>
            <StepItem>1. 등록자 정보</StepItem>
            <StepArrow>→</StepArrow>
            <StepItem>2. 단체 필수 정보</StepItem>
            <StepArrow>→</StepArrow>
            <StepItem>3. 단체 부가 정보</StepItem>
          </StepList>
        </StepIndicator>
      </MainContent>

      <ButtonContainer>
        <NextButton onClick={handleNext} $disabled={!isNextButtonEnabled}>
          다음
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
}

const StepContainer = styled.div`
  margin-top: 28px;
  padding: 16px;
  width: 100%;
  height: calc(100dvh - 55px); /* 헤더 높이 제외 */
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
`;

const MainContent = styled.div`
  flex: 1;
  padding-bottom: 120px; /* 하단 버튼 공간 확보 */
`;

const StepIndicator = styled.div`
  margin-top: 32px;
  padding: 12px;
  background: #e6f3ff;
  border-radius: 8px;
`;

const StepTitle = styled.div`
  color: #007bff;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 12px;
`;

const StepList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const StepItem = styled.div`
  color: #007bff;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
`;

const StepArrow = styled.div`
  color: #007bff;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
`;

const PageTitle = styled.h1`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  height: 24px;
  margin: 0 0 32px 0;
`;

const FormField = styled.div`
  margin-bottom: 30px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  height: 18px;
`;

const RequiredAsterisk = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #f97000;
`;

const LabelText = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #1a1a1a;
`;

const InputField = styled.div<{ $active?: boolean; $disabled?: boolean }>`
  display: flex;
  width: 100%;
  height: 44px;
  padding: 13px 10px;
  align-items: center;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? "#007bff" : "#d1d5d8")};
  background: ${(p) => (p.$disabled ? "#f5f5f5" : "#fdfdfd")};
  &:focus-within {
    border-color: ${(p) => (p.$disabled ? "#d1d5d8" : "#007bff")};
  }
`;

const StyledInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: ${(props) => (props.disabled ? "#999999" : "#1a1a1a")};
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "text")};

  &::placeholder {
    color: #79818a;
  }
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 26px 16px;
  background: linear-gradient(
    0deg,
    #fdfdfd 0%,
    #fdfdfd 60%,
    rgba(253, 253, 253, 0.3) 85%,
    rgba(253, 253, 253, 0) 100%
  );
  backdrop-filter: blur(10px);
  z-index: 1000;
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 800px;
`;

const NextButton = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  height: 60px;
  padding: 13px 10px;
  border-radius: 10px;
  border: none;
  background: ${(props) => (props.$disabled ? "#d1d5d8" : "#004B49")};
  color: white;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${(props) => (props.$disabled ? "#d1d5d8" : "#003a37")};
  }
`;
